<script>
    import { onMount, onDestroy } from "svelte";
    import { functions } from "@app/configs/firebase.js";
    import { httpsCallable } from "firebase/functions";
    import { getStripe } from "@app/configs/stripe.js";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";

    /** @type {{ paymentMethod: { brand: string, last4: string } | null, onCardUpdated: () => void }} */
    let { paymentMethod, onCardUpdated } = $props();

    let cardElement = null;
    let stripeInstance = null;
    let updatingCard = $state(false);
    let cardError = $state(null);

    onMount(async () => {
        try {
            stripeInstance = await getStripe();
            if (!stripeInstance) return;

            const elements = stripeInstance.elements();
            cardElement = elements.create("card", {
                style: {
                    base: {
                        fontSize: "16px",
                        color: "#e0e0e0",
                        "::placeholder": { color: "#6c757d" },
                        fontFamily:
                            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    },
                    invalid: {
                        color: "#dc3545",
                    },
                },
            });

            const container = document.getElementById("stripe-card-element");
            if (container) {
                cardElement.mount(container);
                cardElement.on("change", (event) => {
                    cardError = event.error ? event.error.message : null;
                });
            }
        } catch (err) {
            console.error("Failed to mount Stripe Elements:", err);
            cardError = "Failed to load card input form.";
        }
    });

    onDestroy(() => {
        if (cardElement) {
            try {
                cardElement.unmount();
                cardElement.destroy();
            } catch (e) {
                // Element may already be destroyed
            }
            cardElement = null;
        }
    });

    async function handleUpdateCard() {
        if (!stripeInstance || !cardElement) return;

        updatingCard = true;
        cardError = null;

        try {
            const { paymentMethod: pm, error: stripeError } =
                await stripeInstance.createPaymentMethod({
                    type: "card",
                    card: cardElement,
                });

            if (stripeError) {
                cardError = stripeError.message;
                updatingCard = false;
                return;
            }

            await httpsCallable(functions, "v8UpdatePaymentMethod")({
                paymentMethodId: pm.id,
            });

            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.success(),
                text: m.card_updated_success(),
                icon: "success",
            });

            cardElement.clear();
            onCardUpdated();
        } catch (err) {
            console.error("Failed to update card:", err);
            cardError = err.message || "Failed to update payment method.";
        } finally {
            updatingCard = false;
        }
    }
</script>

<h5 class="mb-4">{m.payment_method_tab()}</h5>

<!-- Current Card -->
<div class="card mb-4">
    <div class="card-body">
        <h6 class="card-title text-muted">{m.current_card()}</h6>
        {#if paymentMethod}
            <div class="d-flex align-items-center">
                <i
                    class="bi bi-credit-card-2-front me-2"
                    style="font-size: 1.5rem;"
                ></i>
                <span class="fs-5">
                    {paymentMethod.brand
                        ? paymentMethod.brand.charAt(0).toUpperCase() +
                          paymentMethod.brand.slice(1)
                        : "Card"}
                    &bull;&bull;&bull;&bull;
                    {paymentMethod.last4}
                </span>
            </div>
        {:else}
            <p class="text-muted mb-0">{m.no_card_on_file()}</p>
        {/if}
    </div>
</div>

<!-- Update Card Form -->
<div class="card">
    <div class="card-body">
        <h6 class="card-title text-muted">{m.new_card_details()}</h6>
        <div
            id="stripe-card-element"
            class="form-control mb-3"
            style="padding: 12px; min-height: 44px;"
        ></div>
        {#if cardError}
            <div class="text-danger small mb-3">
                {cardError}
            </div>
        {/if}
        <button
            class="btn btn-primary"
            onclick={handleUpdateCard}
            disabled={updatingCard}
        >
            {#if updatingCard}
                <span
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                ></span>
                {m.updating_card()}
            {:else}
                <i class="bi bi-credit-card me-1"></i>
                {m.update_card()}
            {/if}
        </button>
    </div>
</div>
